import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ProgramSlideCanvas, { cloneSlideLayout } from '../components/program/ProgramSlideCanvas';
import {
  deleteSectionImage,
  purgeOrphanLandingStorageImages,
  uploadSectionImage,
} from '../services/landingPageService';
import { landingStorageObjectPathFromUrl } from '../utils/landingStoragePaths';
import { getAllIntroSections, saveIntroDrafts } from '../services/landingIntroService';
import AutoStatusToast from '../components/common/AutoStatusToast';
import { useAutoStatusToast } from '../hooks/useAutoStatusToast';
import type { ProgramSlideLayoutV1 } from '../types/programContentSlideCanvas';
import {
  clampLandingIntroHeightRatio,
  emptyContentForKind,
  emptyFeatureContent,
  emptyCenteredTextContent,
  emptyImageBlockContent,
  introImageSrc,
  LANDING_INTRO_DEFAULT_HEIGHT_RATIO,
  parseIntroBlockContent,
  type LandingIntroBlockContent,
  type LandingIntroCenteredTextContent,
  type LandingIntroDraft,
  type LandingIntroFeatureContent,
  type LandingIntroImageBlockContent,
  type LandingIntroImageRef,
  type LandingIntroSectionKind,
} from '../types/landingIntro';
import type { AdminLandingTabActionsChange } from './adminLandingTabActions';
import {
  landingIntroFrameProps,
  landingIntroFrameStyle,
  resolveLandingIntroPageBackground,
} from '../utils/landingIntroPageBackground';

function newDraftId() {
  return `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function sectionKindLabel(kind: LandingIntroSectionKind): string {
  if (kind === 'centered_text') return '중앙 텍스트 블럭';
  if (kind === 'feature_like') return '텍스트 이미지 블럭';
  if (kind === 'image') return '이미지 블럭';
  return '캔버스 블럭';
}

function ActiveCheck({
  active,
  label,
  onToggle,
}: {
  active: boolean;
  label: string;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      className={`cp-admin-landing-faq-check cp-admin-landing-section-active-check${
        active ? ' is-done' : ''
      }`}
      title={active ? '활성 (클릭하여 비활성)' : '비활성 (클릭하여 활성)'}
      aria-pressed={active}
      aria-label={`${label} 활성 여부`}
      onClick={onToggle}
    >
      {active ? (
        <svg
          className="cp-admin-landing-cta-check-mark"
          viewBox="0 0 20 20"
          width="13"
          height="13"
          aria-hidden
          focusable="false"
        >
          <path
            d="M5.05 10.35 L9.25 14.65 L15.35 5.55"
            fill="none"
            stroke="currentColor"
            strokeWidth="3.15"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : null}
    </button>
  );
}

function SortableSlideSection({
  draft,
  index,
  isEditing,
  onStartEdit,
  onStopEdit,
  onChange,
  onLayoutChange,
  onRemove,
}: {
  draft: LandingIntroDraft;
  index: number;
  isEditing: boolean;
  onStartEdit: (id: string) => void;
  onStopEdit: (id: string) => void;
  onChange: (id: string, patch: Partial<LandingIntroDraft>) => void;
  onLayoutChange: (id: string, layout: ProgramSlideLayoutV1) => void;
  onRemove: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: draft.id,
  });
  const frameRef = useRef<HTMLDivElement | null>(null);
  const resizeStartRef = useRef<{ startY: number; startRatio: number; width: number } | null>(null);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2 : undefined,
  };

  const headId = `cp-admin-landing-intro-head-${draft.id}`;
  const label = `섹션 ${index + 1}`;
  const pageBg = resolveLandingIntroPageBackground(draft.layout_json);
  const heightRatio = clampLandingIntroHeightRatio(draft.height_ratio);

  const handleResizePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!isEditing) return;
    e.preventDefault();
    e.stopPropagation();
    const frame = frameRef.current;
    if (!frame) return;
    const width = frame.getBoundingClientRect().width;
    if (width <= 0) return;
    resizeStartRef.current = {
      startY: e.clientY,
      startRatio: heightRatio,
      width,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handleResizePointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const start = resizeStartRef.current;
    if (!start) return;
    const deltaY = e.clientY - start.startY;
    const next = clampLandingIntroHeightRatio(start.startRatio + deltaY / start.width);
    onChange(draft.id, { height_ratio: next });
  };

  const handleResizePointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!resizeStartRef.current) return;
    resizeStartRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  return (
    <article
      ref={setNodeRef}
      style={style}
      className={`cp-dashboard-block cp-admin-landing-section-block${
        isDragging ? ' is-dragging' : ''
      }${draft.is_active ? '' : ' is-section-inactive'}`}
      aria-labelledby={headId}
    >
      <h3 className="cp-dashboard-block__head" id={headId}>
        <span className="cp-admin-landing-section-head-side cp-admin-landing-section-head-side--left">
          <ActiveCheck
            active={draft.is_active}
            label={label}
            onToggle={() => onChange(draft.id, { is_active: !draft.is_active })}
          />
          <button
            type="button"
            className="cp-admin-landing-faq-drag"
            title="드래그하여 순서 변경"
            aria-label={`${label} 순서 변경`}
            {...attributes}
            {...listeners}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
            </svg>
          </button>
        </span>
        <span className="cp-dashboard-block__head-title">
          {sectionKindLabel('slide')}
          <span className="cp-admin-landing-section-order">순서 {index + 1}</span>
        </span>
        <span className="cp-admin-landing-section-head-side cp-admin-landing-section-head-side--actions">
          {isEditing ? (
            <button
              type="button"
              className="cp-btn-secondary cp-admin-landing-edit-btn"
              onClick={() => onStopEdit(draft.id)}
            >
              편집 완료
            </button>
          ) : (
            <button
              type="button"
              className="cp-btn-secondary cp-admin-landing-edit-btn"
              onClick={() => onStartEdit(draft.id)}
            >
              편집
            </button>
          )}
          <button
            type="button"
            className="cp-admin-landing-faq-remove"
            onClick={() => onRemove(draft.id)}
            title="삭제"
            aria-label={`${label} 삭제`}
          >
            <img
              src={`${process.env.PUBLIC_URL || ''}/Trash.svg`}
              alt=""
              width={14}
              height={14}
              className="cp-todo-note-trash-icon"
            />
          </button>
        </span>
      </h3>

      <div className="cp-dashboard-block__body cp-admin-landing-section-body">
        <div className="cp-program-slide-root cp-admin-landing-intro-slide-root" data-slide-surface="light">
          <div
            ref={frameRef}
            className={`landing-intro-section-frame cp-admin-landing-intro-frame${
              isEditing ? ' is-editing' : ''
            }`}
            style={landingIntroFrameStyle(heightRatio, pageBg)}
            {...landingIntroFrameProps(pageBg)}
          >
            <ProgramSlideCanvas
              layout={draft.layout_json}
              onLayoutChange={(next) => onLayoutChange(draft.id, next)}
              readOnly={!isEditing}
              viewMode={!isEditing}
              canvasAspectRatio="presentation"
              toolbarPreset="full"
              imageStorageKind="slide"
              defaultTextColorHex={draft.layout_json.defaultTextColorHex}
              canvasBackgroundColorHex={pageBg}
            />
            {isEditing ? (
              <button
                type="button"
                className="landing-intro-height-resize"
                aria-label={`${label} 높이 조절`}
                title="드래그하여 높이 조절"
                onPointerDown={handleResizePointerDown}
                onPointerMove={handleResizePointerMove}
                onPointerUp={handleResizePointerUp}
                onPointerCancel={handleResizePointerUp}
              />
            ) : null}
          </div>
        </div>
      </div>
    </article>
  );
}

const DEFAULT_BLOCK_CTA_LABEL = '버튼';

function uploadErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    return String((err as { message: unknown }).message || '알 수 없는 오류');
  }
  if (err instanceof Error) return err.message;
  return '알 수 없는 오류';
}

async function uploadIntroBlockImage(file: File): Promise<LandingIntroImageRef> {
  const key = `intro_block_${Date.now()}`;
  const { path, url } = await uploadSectionImage(key, file);
  return { path, url, version: Date.now(), alt: '' };
}

function forgetIntroStorageImage(img: LandingIntroImageRef | null | undefined) {
  const path = img?.path?.trim() || landingStorageObjectPathFromUrl(img?.url ?? '') || '';
  if (!path) return;
  void deleteSectionImage(path).catch((err) => {
    console.warn('소개 이미지 스토리지 삭제 실패:', err);
  });
}

function BlockImageSlot({
  src,
  alt,
  isEditing,
  uploading,
  fileInputId,
  onFile,
  onClear,
}: {
  src: string | null;
  alt: string;
  isEditing: boolean;
  uploading: boolean;
  fileInputId: string;
  onFile: (file: File | null) => void;
  onClear?: () => void;
}) {
  return (
    <div className="cp-admin-landing-image-panel">
      <div className={`cp-admin-landing-image-block${isEditing ? ' is-editing' : ''}`}>
        {src ? (
          <div className="landing-feature-image-wrap cp-admin-landing-image-wrap">
            <img src={src} alt={alt} className="landing-feature-image" />
            {isEditing && onClear ? (
              <button
                type="button"
                className="cp-btn-danger cp-admin-landing-image-corner-remove"
                onClick={onClear}
                disabled={uploading}
              >
                삭제
              </button>
            ) : null}
          </div>
        ) : (
          <div className="landing-feature-image-placeholder cp-admin-landing-image-placeholder">
            <span className="landing-feature-image-placeholder-text">이미지 준비중</span>
          </div>
        )}
        {isEditing ? (
          <div className="cp-admin-landing-image-block-actions">
            <input
              type="file"
              accept="image/*"
              className="cp-admin-landing-file-input"
              id={fileInputId}
              disabled={uploading}
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                onFile(file);
                e.target.value = '';
              }}
            />
            <label htmlFor={fileInputId} className="cp-admin-landing-file-btn">
              {uploading ? '업로드 중…' : '이미지 선택'}
            </label>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function FeatureBlockPreview({
  draft,
  index,
  isEditing,
  onContentChange,
}: {
  draft: LandingIntroDraft;
  index: number;
  isEditing: boolean;
  onContentChange: (content: LandingIntroBlockContent) => void;
}) {
  const c = parseIntroBlockContent('feature_like', draft.content_json) as LandingIntroFeatureContent;
  const src = introImageSrc(c.image);
  const [uploading, setUploading] = useState(false);
  const fileId = `cp-admin-intro-file-${draft.id}`;
  const ctaOn = Boolean((c.ctaLabel || '').trim());
  /** reverse=true → 이미지 오른쪽 (공개 페이지 rtl) */
  const reverse = c.reverse ?? index % 2 === 0;

  const patch = (next: LandingIntroFeatureContent) => onContentChange(next);

  return (
    <div className="cp-admin-landing-intro-block">
      {isEditing ? (
        <div
          className="cp-admin-landing-intro-section-options"
          role="group"
          aria-label="좌우 반전"
        >
          <button
            type="button"
            className={`cp-admin-landing-intro-mode-btn${!reverse ? ' is-active' : ''}`}
            onClick={() => patch({ ...c, reverse: false })}
          >
            왼쪽 이미지
          </button>
          <button
            type="button"
            className={`cp-admin-landing-intro-mode-btn${reverse ? ' is-active' : ''}`}
            onClick={() => patch({ ...c, reverse: true })}
          >
            오른쪽 이미지
          </button>
        </div>
      ) : null}

      <div className="cp-admin-landing-preview cp-admin-landing-preview--feature">
        <div className={`cp-admin-landing-feature-frame${reverse ? ' landing-feature--reverse' : ''}`}>
          <div className="landing-feature-content">
            {/* 공개 페이지와 동일: 이미지 → 텍스트 (reverse 시 이미지 오른쪽) */}
            <BlockImageSlot
              src={src}
              alt={c.title || '소개 이미지'}
              isEditing={isEditing}
              uploading={uploading}
              fileInputId={fileId}
              onFile={(file) => {
                if (!file) return;
                setUploading(true);
                void uploadIntroBlockImage(file)
                  .then((img) => {
                    forgetIntroStorageImage(c.image);
                    patch({ ...c, image: img });
                  })
                  .catch((err) => {
                    console.error(err);
                    window.alert(`이미지 업로드에 실패했습니다.\n${uploadErrorMessage(err)}`);
                  })
                  .finally(() => setUploading(false));
              }}
              onClear={() => {
                forgetIntroStorageImage(c.image);
                patch({ ...c, image: null });
              }}
            />

            <div className="cp-admin-landing-preview-text landing-feature-text">
              {isEditing ? (
                <>
                  <textarea
                    className="cp-admin-landing-edit-input landing-feature-title"
                    value={c.title}
                    onChange={(e) => patch({ ...c, title: e.target.value })}
                    aria-label="제목"
                    rows={2}
                    placeholder="제목"
                  />
                  <textarea
                    className="cp-admin-landing-edit-input landing-feature-subtitle"
                    value={c.subtitle}
                    onChange={(e) => patch({ ...c, subtitle: e.target.value })}
                    aria-label="부제목"
                    rows={1}
                    placeholder="부제목"
                  />
                  <textarea
                    className="cp-admin-landing-edit-input landing-feature-body"
                    value={c.body}
                    onChange={(e) => patch({ ...c, body: e.target.value })}
                    aria-label="본문"
                    rows={3}
                    placeholder="본문"
                  />
                </>
              ) : (
                <>
                  {c.title ? <h2 className="landing-feature-title">{c.title}</h2> : null}
                  {c.subtitle ? <p className="landing-feature-subtitle">{c.subtitle}</p> : null}
                  {c.body ? <p className="landing-feature-body">{c.body}</p> : null}
                  {!c.title && !c.subtitle && !c.body ? (
                    <p className="cp-dashboard-placeholder">텍스트를 입력하려면 편집하세요.</p>
                  ) : null}
                </>
              )}

              {isEditing ? (
                <div className="cp-admin-landing-cta-row cp-admin-landing-cta-row--feature is-editing">
                  <div
                    className={`cp-admin-landing-cta-btn-block cp-admin-landing-feature-cta${
                      ctaOn ? '' : ' is-ghost'
                    }`}
                  >
                    <button
                      type="button"
                      className={`cp-admin-landing-cta-check${ctaOn ? ' is-done' : ''}`}
                      title="버튼 표시"
                      aria-pressed={ctaOn}
                      onClick={() =>
                        patch({
                          ...c,
                          ctaLabel: ctaOn ? '' : c.ctaLabel?.trim() || DEFAULT_BLOCK_CTA_LABEL,
                        })
                      }
                    >
                      {ctaOn ? (
                        <svg
                          className="cp-admin-landing-cta-check-mark"
                          viewBox="0 0 20 20"
                          width="13"
                          height="13"
                          aria-hidden
                          focusable="false"
                        >
                          <path
                            d="M5.05 10.35 L9.25 14.65 L15.35 5.55"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3.15"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : null}
                    </button>
                    <input
                      type="text"
                      className="cp-admin-landing-edit-input cp-admin-landing-edit-input--btn-label"
                      value={c.ctaLabel || DEFAULT_BLOCK_CTA_LABEL}
                      onChange={(e) => patch({ ...c, ctaLabel: e.target.value })}
                      disabled={!ctaOn}
                      aria-label="버튼 텍스트"
                      placeholder="버튼 텍스트"
                    />
                  </div>
                  <input
                    type="text"
                    className="cp-admin-landing-edit-input cp-admin-landing-cta-link"
                    value={c.ctaLink || ''}
                    onChange={(e) => patch({ ...c, ctaLink: e.target.value })}
                    disabled={!ctaOn}
                    aria-label="버튼 링크"
                    placeholder="/login 또는 URL"
                  />
                </div>
              ) : ctaOn ? (
                <div className="cp-admin-landing-cta-row cp-admin-landing-cta-row--feature">
                  <span className="cp-admin-landing-feature-cta" aria-hidden>
                    {c.ctaLabel || DEFAULT_BLOCK_CTA_LABEL}
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function syncAutoGrowTextarea(el: HTMLTextAreaElement | null) {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${Math.max(el.scrollHeight, 0)}px`;
}

function CenteredAutoGrowField({
  className,
  value,
  onChange,
  placeholder,
  ariaLabel,
  minRows,
}: {
  className: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  ariaLabel: string;
  minRows: number;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    syncAutoGrowTextarea(ref.current);
  }, [value]);

  return (
    <textarea
      ref={ref}
      className={`${className} cp-admin-landing-intro-centered-autogrow`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onInput={(e) => syncAutoGrowTextarea(e.currentTarget)}
      rows={minRows}
      placeholder={placeholder}
      aria-label={ariaLabel}
    />
  );
}

function CenteredTextBlockPreview({
  draft,
  isEditing,
  onContentChange,
}: {
  draft: LandingIntroDraft;
  isEditing: boolean;
  onContentChange: (content: LandingIntroBlockContent) => void;
}) {
  const c = parseIntroBlockContent(
    'centered_text',
    draft.content_json,
  ) as LandingIntroCenteredTextContent;
  const isEmpty = !c.title.trim() && !c.subtitle.trim() && !c.body.trim();

  return (
    <div className="cp-admin-landing-intro-block cp-admin-landing-intro-centered-preview">
      <div
        className={`cp-admin-landing-intro-centered-stack${
          !isEditing && isEmpty ? ' is-empty' : ''
        }`}
      >
        {isEditing ? (
          <>
            <CenteredAutoGrowField
              className="cp-admin-landing-edit-input landing-intro-centered-title"
              value={c.title}
              onChange={(title) => onContentChange({ ...c, title })}
              minRows={1}
              placeholder="제목"
              ariaLabel="제목"
            />
            <CenteredAutoGrowField
              className="cp-admin-landing-edit-input landing-intro-centered-subtitle"
              value={c.subtitle}
              onChange={(subtitle) => onContentChange({ ...c, subtitle })}
              minRows={1}
              placeholder="부제목"
              ariaLabel="부제목"
            />
            <CenteredAutoGrowField
              className="cp-admin-landing-edit-input landing-intro-centered-body"
              value={c.body}
              onChange={(body) => onContentChange({ ...c, body })}
              minRows={2}
              placeholder="내용"
              ariaLabel="내용"
            />
          </>
        ) : (
          <section
            className={`landing-intro-centered-text${
              isEmpty ? ' landing-intro-centered-text--empty' : ''
            }`}
          >
            {c.title ? <h2 className="landing-intro-centered-title">{c.title}</h2> : null}
            {c.subtitle ? <p className="landing-intro-centered-subtitle">{c.subtitle}</p> : null}
            {c.body ? <p className="landing-intro-centered-body">{c.body}</p> : null}
            {isEmpty ? (
              <p className="cp-admin-landing-intro-centered-empty-hint">텍스트를 입력하세요.</p>
            ) : null}
          </section>
        )}
      </div>
    </div>
  );
}

function ImageBlockPreview({
  draft,
  isEditing,
  onContentChange,
}: {
  draft: LandingIntroDraft;
  isEditing: boolean;
  onContentChange: (content: LandingIntroBlockContent) => void;
}) {
  const c = parseIntroBlockContent('image', draft.content_json) as LandingIntroImageBlockContent;
  const [uploading, setUploading] = useState(false);
  const fileId = `cp-admin-intro-images-${draft.id}`;
  const cols = c.columns === 3 ? 3 : 2;

  const removeAt = (i: number) => {
    forgetIntroStorageImage(c.images[i]);
    onContentChange({
      ...c,
      images: c.images.filter((_, idx) => idx !== i),
    });
  };

  return (
    <div className="cp-admin-landing-intro-block cp-admin-landing-intro-image-preview">
      {isEditing ? (
        <div
          className="cp-admin-landing-intro-section-options"
          role="group"
          aria-label="이미지 배치"
        >
          <button
            type="button"
            className={`cp-admin-landing-intro-mode-btn${c.display === 'carousel' ? ' is-active' : ''}`}
            onClick={() => onContentChange({ ...c, display: 'carousel' })}
          >
            캐러셀
          </button>
          {c.display === 'grid' ? (
            <div
              className="cp-admin-landing-intro-mode-btn cp-admin-landing-intro-mode-btn--expanded is-active"
              role="group"
              aria-label="바둑판 열 수"
            >
              <span className="cp-admin-landing-intro-mode-btn-label">바둑판</span>
              <div className="cp-admin-landing-intro-mode-subbtns">
                <button
                  type="button"
                  className={`cp-admin-landing-intro-mode-subbtn${cols === 2 ? ' is-active' : ''}`}
                  onClick={() => onContentChange({ ...c, display: 'grid', columns: 2 })}
                >
                  2열
                </button>
                <button
                  type="button"
                  className={`cp-admin-landing-intro-mode-subbtn${cols === 3 ? ' is-active' : ''}`}
                  onClick={() => onContentChange({ ...c, display: 'grid', columns: 3 })}
                >
                  3열
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="cp-admin-landing-intro-mode-btn"
              onClick={() => onContentChange({ ...c, display: 'grid' })}
            >
              바둑판
            </button>
          )}
        </div>
      ) : null}

      <div className="cp-admin-landing-intro-image-stage">
        {c.display === 'grid' ? (
          <div
            className="landing-intro-image-grid"
            style={{ ['--landing-intro-grid-cols' as string]: String(cols) }}
          >
            {c.images.length === 0 ? (
              <div className="landing-intro-image-placeholder cp-admin-landing-intro-image-empty">
                이미지 준비중
              </div>
            ) : (
              c.images.map((img, i) => {
                const src = introImageSrc(img);
                return (
                  <figure
                    key={`${img.path ?? img.url ?? i}-${i}`}
                    className="landing-intro-image-grid-item"
                  >
                    {src ? (
                      <img src={src} alt={img.alt || `이미지 ${i + 1}`} />
                    ) : (
                      <div className="landing-intro-image-placeholder">이미지 준비중</div>
                    )}
                    {isEditing ? (
                      <button
                        type="button"
                        className="cp-btn-danger cp-admin-landing-intro-grid-remove"
                        onClick={() => removeAt(i)}
                      >
                        삭제
                      </button>
                    ) : null}
                  </figure>
                );
              })
            )}
          </div>
        ) : (
          <div className="cp-admin-landing-intro-carousel-row">
            {c.images.length === 0 ? (
              <div className="landing-intro-image-placeholder cp-admin-landing-intro-image-empty">
                이미지 준비중
              </div>
            ) : (
              c.images.map((img, i) => {
                const src = introImageSrc(img);
                return (
                  <figure
                    key={`${img.path ?? img.url ?? i}-${i}`}
                    className="cp-admin-landing-intro-carousel-item"
                  >
                    {src ? (
                      <img src={src} alt={img.alt || `이미지 ${i + 1}`} />
                    ) : (
                      <div className="landing-intro-image-placeholder">이미지 준비중</div>
                    )}
                    {isEditing ? (
                      <button
                        type="button"
                        className="cp-btn-danger cp-admin-landing-intro-grid-remove"
                        onClick={() => removeAt(i)}
                      >
                        삭제
                      </button>
                    ) : null}
                  </figure>
                );
              })
            )}
          </div>
        )}

        {isEditing ? (
          <div className="cp-admin-landing-intro-image-actions">
            <input
              type="file"
              accept="image/*"
              className="cp-admin-landing-file-input"
              id={fileId}
              disabled={uploading}
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                e.target.value = '';
                if (!file) return;
                setUploading(true);
                void uploadIntroBlockImage(file)
                  .then((img) => onContentChange({ ...c, images: [...c.images, img] }))
                  .catch((err) => {
                    console.error(err);
                    window.alert(`이미지 업로드에 실패했습니다.\n${uploadErrorMessage(err)}`);
                  })
                  .finally(() => setUploading(false));
              }}
            />
            <label htmlFor={fileId} className="cp-admin-landing-file-btn">
              {uploading ? '업로드 중…' : '이미지 선택'}
            </label>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function SortableBlockSection({
  draft,
  index,
  isEditing,
  onStartEdit,
  onStopEdit,
  onChange,
  onRemove,
}: {
  draft: LandingIntroDraft;
  index: number;
  isEditing: boolean;
  onStartEdit: (id: string) => void;
  onStopEdit: (id: string) => void;
  onChange: (id: string, patch: Partial<LandingIntroDraft>) => void;
  onRemove: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: draft.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2 : undefined,
  };
  const headId = `cp-admin-landing-intro-block-${draft.id}`;
  const label = `${sectionKindLabel(draft.section_kind)} ${index + 1}`;
  const onContentChange = (content_json: LandingIntroBlockContent) =>
    onChange(draft.id, { content_json });

  return (
    <article
      ref={setNodeRef}
      style={style}
      className={`cp-dashboard-block cp-admin-landing-section-block${
        isDragging ? ' is-dragging' : ''
      }${draft.is_active ? '' : ' is-section-inactive'}`}
      aria-labelledby={headId}
    >
      <h3 className="cp-dashboard-block__head" id={headId}>
        <span className="cp-admin-landing-section-head-side cp-admin-landing-section-head-side--left">
          <ActiveCheck
            active={draft.is_active}
            label={label}
            onToggle={() => onChange(draft.id, { is_active: !draft.is_active })}
          />
          <button
            type="button"
            className="cp-admin-landing-faq-drag"
            title="드래그하여 순서 변경"
            aria-label={`${label} 순서 변경`}
            {...attributes}
            {...listeners}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
            </svg>
          </button>
        </span>
        <span className="cp-dashboard-block__head-title">
          {sectionKindLabel(draft.section_kind)}
          <span className="cp-admin-landing-section-order">순서 {index + 1}</span>
        </span>
        <span className="cp-admin-landing-section-head-side cp-admin-landing-section-head-side--actions">
          {isEditing ? (
            <button
              type="button"
              className="cp-btn-secondary cp-admin-landing-edit-btn"
              onClick={() => onStopEdit(draft.id)}
            >
              편집 완료
            </button>
          ) : (
            <button
              type="button"
              className="cp-btn-secondary cp-admin-landing-edit-btn"
              onClick={() => onStartEdit(draft.id)}
            >
              편집
            </button>
          )}
          <button
            type="button"
            className="cp-admin-landing-faq-remove"
            onClick={() => onRemove(draft.id)}
            title="삭제"
            aria-label={`${label} 삭제`}
          >
            <img
              src={`${process.env.PUBLIC_URL || ''}/Trash.svg`}
              alt=""
              width={14}
              height={14}
              className="cp-todo-note-trash-icon"
            />
          </button>
        </span>
      </h3>
      <div className="cp-dashboard-block__body cp-admin-landing-section-body">
        {draft.section_kind === 'feature_like' ? (
          <FeatureBlockPreview
            draft={draft}
            index={index}
            isEditing={isEditing}
            onContentChange={onContentChange}
          />
        ) : draft.section_kind === 'centered_text' ? (
          <CenteredTextBlockPreview
            draft={draft}
            isEditing={isEditing}
            onContentChange={onContentChange}
          />
        ) : draft.section_kind === 'image' ? (
          <ImageBlockPreview
            draft={draft}
            isEditing={isEditing}
            onContentChange={onContentChange}
          />
        ) : null}
      </div>
    </article>
  );
}

const AdminLandingIntroEditor: React.FC<{
  onActionsChange?: AdminLandingTabActionsChange;
}> = ({ onActionsChange }) => {
  const [drafts, setDrafts] = useState<LandingIntroDraft[]>([]);
  const [deletedIds, setDeletedIds] = useState<string[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const { toastState: saveToastState, showSuccess: showSaveSuccess } = useAutoStatusToast(3000);

  const applyServer = useCallback(
    async (items: Awaited<ReturnType<typeof getAllIntroSections>>) => {
      setDrafts(
        items.map((it) => ({
          id: it.id,
          section_kind: it.section_kind,
          layout_json: it.layout_json,
          content_json: it.content_json ?? emptyContentForKind(it.section_kind),
          height_ratio: it.height_ratio,
          is_active: it.is_active,
        })),
      );
      setDeletedIds([]);
      setDirty(false);
      setEditingId(null);
      setAddModalOpen(false);
    },
    [],
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAllIntroSections();
      await applyServer(data);
      setError(null);
    } catch (err) {
      console.error(err);
      setError(
        '서비스 소개를 불러올 수 없습니다. 마이그레이션(section_kind / content_json) 적용 여부를 확인해 주세요.',
      );
    } finally {
      setLoading(false);
    }
  }, [applyServer]);

  useEffect(() => {
    void load();
  }, [load]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const ids = useMemo(() => drafts.map((d) => d.id), [drafts]);
  const markDirty = () => setDirty(true);

  const patchDraft = (id: string, patch: Partial<LandingIntroDraft>) => {
    setDrafts((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)));
    markDirty();
  };

  const patchLayout = (id: string, layout: ProgramSlideLayoutV1) => {
    setDrafts((prev) => prev.map((d) => (d.id === id ? { ...d, layout_json: layout } : d)));
    markDirty();
  };

  const removeDraft = (id: string) => {
    const target = drafts.find((d) => d.id === id);
    if (!target) return;
    if (!window.confirm('이 섹션을 삭제할까요?')) return;
    setDrafts((prev) => prev.filter((d) => d.id !== id));
    if (!target.isNew) {
      setDeletedIds((prev) => [...prev, id]);
    }
    setEditingId((prev) => (prev === id ? null : prev));
    markDirty();
  };

  const addSlideDraft = useCallback(() => {
    const id = newDraftId();
    setDrafts((prev) => [
      ...prev,
      {
        id,
        section_kind: 'slide',
        layout_json: cloneSlideLayout(undefined),
        content_json: emptyContentForKind('slide'),
        height_ratio: LANDING_INTRO_DEFAULT_HEIGHT_RATIO,
        is_active: true,
        isNew: true,
      },
    ]);
    setEditingId(id);
    setAddModalOpen(false);
    markDirty();
  }, []);

  const addBlockDraft = useCallback((kind: LandingIntroSectionKind) => {
    if (kind === 'slide') {
      addSlideDraft();
      return;
    }
    const id = newDraftId();
    const content =
      kind === 'feature_like'
        ? emptyFeatureContent()
        : kind === 'centered_text'
          ? emptyCenteredTextContent()
          : emptyImageBlockContent();
    setDrafts((prev) => [
      ...prev,
      {
        id,
        section_kind: kind,
        layout_json: cloneSlideLayout(undefined),
        content_json: content,
        height_ratio: LANDING_INTRO_DEFAULT_HEIGHT_RATIO,
        is_active: true,
        isNew: true,
      },
    ]);
    setEditingId(id);
    setAddModalOpen(false);
    markDirty();
  }, [addSlideDraft]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = drafts.findIndex((d) => d.id === String(active.id));
    const newIndex = drafts.findIndex((d) => d.id === String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    setDrafts((prev) => arrayMove(prev, oldIndex, newIndex));
    markDirty();
  };

  const handleDiscard = useCallback(() => {
    if (dirty && !window.confirm('저장하지 않은 변경을 모두 취소할까요?')) return;
    void load();
  }, [dirty, load]);

  const handleSave = useCallback(async () => {
    if (!dirty) return;
    try {
      setSaving(true);
      const fresh = await saveIntroDrafts(drafts, deletedIds);
      await applyServer(fresh);
      try {
        await purgeOrphanLandingStorageImages();
      } catch (purgeErr) {
        console.warn('랜딩 고아 이미지 정리 실패:', purgeErr);
      }
      showSaveSuccess('서비스 소개가 저장되었습니다.');
    } catch (err) {
      console.error(err);
      const detail = uploadErrorMessage(err);
      window.alert(
        `저장에 실패했습니다.\n${detail}\n\n마이그레이션(section_kind / content_json) 적용 여부를 확인해 주세요.`,
      );
    } finally {
      setSaving(false);
    }
  }, [applyServer, deletedIds, dirty, drafts, showSaveSuccess]);

  const handleAdd = useCallback(() => {
    setAddModalOpen(true);
  }, []);

  useEffect(() => {
    if (!addModalOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAddModalOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [addModalOpen]);

  useEffect(() => {
    if (!onActionsChange) return;
    if (loading || error) {
      onActionsChange(null);
      return;
    }
    onActionsChange({
      dirty,
      saving,
      onDiscard: handleDiscard,
      onSave: () => {
        void handleSave();
      },
      onAdd: handleAdd,
      addLabel: '블록 추가',
    });
    return () => onActionsChange(null);
  }, [
    dirty,
    error,
    handleAdd,
    handleDiscard,
    handleSave,
    loading,
    onActionsChange,
    saving,
  ]);

  if (loading) {
    return (
      <p className="cp-dashboard-placeholder" role="status">
        로딩 중…
      </p>
    );
  }

  if (error) {
    return (
      <div>
        <p className="cp-dashboard-placeholder" role="alert">
          {error}
        </p>
        <button type="button" className="cp-btn-primary" onClick={() => void load()}>
          다시 시도
        </button>
      </div>
    );
  }

  return (
    <div className="cp-admin-landing-intro">
      <AutoStatusToast state={saveToastState} />
      {addModalOpen ? (
        <div
          className="cp-modal-backdrop"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setAddModalOpen(false);
          }}
        >
          <div
            className="cp-modal cp-admin-landing-intro-add-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-admin-landing-intro-add-title"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h3 id="cp-admin-landing-intro-add-title">블록 종류 선택</h3>
            <div className="cp-modal-body">
              <p className="cp-admin-landing-intro-add-modal-hint">추가할 블록 종류를 선택하세요.</p>
              <div
                className="cp-admin-landing-intro-add-modal-choices"
                role="group"
                aria-label="블록 종류"
              >
                <button
                  type="button"
                  className="cp-admin-landing-intro-add-choice"
                  onClick={() => addBlockDraft('centered_text')}
                >
                  중앙 텍스트 블럭
                </button>
                <button
                  type="button"
                  className="cp-admin-landing-intro-add-choice"
                  onClick={() => addBlockDraft('feature_like')}
                >
                  텍스트 이미지 블럭
                </button>
                <button
                  type="button"
                  className="cp-admin-landing-intro-add-choice"
                  onClick={() => addBlockDraft('image')}
                >
                  이미지 블럭
                </button>
                <button
                  type="button"
                  className="cp-admin-landing-intro-add-choice"
                  onClick={() => addBlockDraft('slide')}
                >
                  캔버스 블럭
                </button>
              </div>
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={() => setAddModalOpen(false)}>
                취소
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {dirty ? (
        <p className="cp-admin-landing-dirty-hint" role="status">
          저장되지 않은 변경이 있습니다. 「저장하기」를 눌러 반영하세요.
        </p>
      ) : null}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <div className="cp-admin-landing-sections">
            {drafts.length === 0 ? (
              <p className="cp-dashboard-placeholder">
                블록이 없습니다. 「블록 추가」로 중앙 텍스트·텍스트 이미지·이미지·캔버스 블럭을 등록하세요.
              </p>
            ) : (
              drafts.map((draft, index) =>
                draft.section_kind === 'slide' ? (
                  <SortableSlideSection
                    key={draft.id}
                    draft={draft}
                    index={index}
                    isEditing={editingId === draft.id}
                    onStartEdit={setEditingId}
                    onStopEdit={() => setEditingId(null)}
                    onChange={patchDraft}
                    onLayoutChange={patchLayout}
                    onRemove={removeDraft}
                  />
                ) : (
                  <SortableBlockSection
                    key={draft.id}
                    draft={draft}
                    index={index}
                    isEditing={editingId === draft.id}
                    onStartEdit={setEditingId}
                    onStopEdit={() => setEditingId(null)}
                    onChange={patchDraft}
                    onRemove={removeDraft}
                  />
                ),
              )
            )}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default AdminLandingIntroEditor;
